import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './NewsArticlesPage.css';
import moment from 'moment';

const baseUrl = process.env.REACT_APP_NCLUDE_API;
const reactBaseUrl = process.env.REACT_APP_BASE_URL;

function NewsArticlesPage() {
    const { language, source, category } = useParams();
    const navigate = useNavigate();

    const [selectedLanguage, setSelectedLanguage] = useState(language.toLowerCase() === 'hindi' ? 'hin' : 'eng');
    const [articles, setArticles] = useState(null);
    const [playingArticleId, setPlayingArticleId] = useState(null);
    const [audioElement, setAudioElement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showLoadMore, setShowLoadMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    const booksPerPage = 5;


    useEffect(() => {
        fetchArticles(1, true);
    }, [language, source]);

    async function fetchArticles(page, reset = false) {
        setCurrentPage(1);
        try {
            setLoading(true);
            let response = await axios.get(`${baseUrl}/v1/api/news/get-latest-news?page=1&lang=${selectedLanguage}&category=${category}&source=${source}&page=${page}&limit=${booksPerPage}`);
            const newArticles = response.data.articles || [];
            if (reset) {
                setArticles(response.data.articles || []);
            } else {
                setArticles(prevArticles => [...prevArticles, ...newArticles]);
            }
            setShowLoadMore(true);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching articles:", error);
        } finally {
            setLoading(false);
        }
    }

    const handlePlay = (articleId, audio) => {
        if (audioElement) {
            audioElement.pause();
            setAudioElement(null); // Reset audio element
        }

        setAudioElement(audio);
        setPlayingArticleId(articleId);
        audio.play(); // Start playing the new audio
    };

    const handleLoadMore = async () => {
        fetchArticles(currentPage + 1)
    };
    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <div className='container'>
                <section className="header-wrap d-flex justify-content-start align-items-center">
                    <button onClick={() => navigate(-1)} className="btn btn-secondary">
                        Go Back
                    </button>
                    <h1 className="logo my-auto ms-4">
                        <span className="assistive-text">Shravan Audiobooks Library</span>
                    </h1>
                </section>

                {articles && articles.length > 0 ? (
                    <section className="audiobooks-container mt-5">
                        <div className="audio-book-feed" id="secondary-content">
                            <header>
                                <h3 className="title text-start">
                                    Select an Article from {source} under category {category}
                                </h3>
                            </header>
                            <ul className='list-group audiobooks-list mt-3'>
                                {articles.map((article, index) => (
                                    <li key={article._id} className='list-group-item audiobook-item'>
                                        <div className="result-data d-flex justify-content-between align-items-baseline">
                                            <Link to={`/category/${language}/news/${source}/${category}/articles`}
                                                style={{ textDecoration: 'none', color: 'inherit' }}
                                            >
                                                <p className='mb-0'>
                                                    {index + 1}. {moment(article.date, 'YYYY-MM-DD').format('DD-MMM-YYYY')} - {article.title}
                                                </p>
                                            </Link>
                                            <PlayArticle
                                                article={article}
                                                isPlaying={playingArticleId === article._id}
                                                language={selectedLanguage}
                                                onPlay={(audio) => handlePlay(article._id, audio)}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {showLoadMore && (
                                <button className="load-more-button btn btn-primary" onClick={handleLoadMore} disabled={loading}>
                                    {loading ? 'Loading...' : 'Load More'}
                                </button>
                            )}
                        </div>
                    </section>
                ) : (
                    <div className="empty-container mt-5">
                        {articles && articles.length === 0 ? (
                            <p className='text-left'>No news articles found</p>
                        ) : <p>Loading...</p>}

                    </div>
                )}

            </div>
        </div>
    );
}

function PlayArticle({ article, language, isPlaying, onPlay }) {
    const [isPaused, setIsPaused] = useState(true);
    const [loading, setLoading] = useState(false);
    const audioRef = useRef(null);
    const liveRegionRef = useRef(null);

    const announceMessage = (message) => {
        if (liveRegionRef.current) {
            liveRegionRef.current.textContent = '';
            setTimeout(() => {
                liveRegionRef.current.textContent = message;
            }, 100);
        }
    };

    const handlePlay = async () => {
        try {
            setLoading(true);
            announceMessage('Loading audio...');
            setTimeout(async () => {
                try {
                    const response = await axios.post(`${reactBaseUrl}/ivr/stream-news-audio`, {
                        lang: language == 'hin' ? 'hindi' : 'english',
                        date: article.date,
                        source: article.source,
                        category: article.category,
                        url: article.url,
                        body: article.body
                    }, { responseType: 'blob' });

                    setLoading(false);
                    announceMessage('Audio loaded, ready to play.');
                    const audioUrl = URL.createObjectURL(response.data);
                    audioRef.current.src = audioUrl;
                    onPlay(audioRef.current);
                    audioRef.current.play().then(() => {
                        setIsPaused(false);
                    }).catch(error => console.error('Error attempting to play:', error));
                    // savePlaybackProgress(currentTime);

                    audioRef.current.onended = () => setIsPaused(true);
                    audioRef.current.onpause = () => setIsPaused(true);
                } catch (err) {
                    setLoading(false);
                    announceMessage('Failed to load audio.');
                    console.error("Error playing article audio:", err);
                }
            }, 100);
        } catch (err) {
            setLoading(false);
            announceMessage('Failed to load audio.');
            console.error("Error preparing to play article audio:", err);
        }
    };

    const handlePause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPaused(true);
        }
    };

    // if (audioRef != null && audioRef.current) {
    //     await handlePlayNext();
    // }

    return (
        <>
            <audio ref={audioRef}>
                <source type="audio/mp3" />
                Your browser does not support the audio element.
            </audio>
            <div
                ref={liveRegionRef}
                aria-live="polite"
                role="status"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
            ></div>
            <div className='d-flex flex-wrap'>
                {loading ? (
                    <div className="spinner-border text-primary" role="status"></div>
                ) : (
                    <Button
                        className='mx-2'
                        variant="primary"
                        onClick={isPaused ? handlePlay : handlePause}
                        aria-label={loading ? 'Loading audio, please wait' : isPaused ? `Play ${article.title}` : `Pause ${article.title}`}
                    >
                        {isPaused ? 'Play' : 'Pause'}
                    </Button>
                )}
            </div>
        </>
    );
}

export default NewsArticlesPage;
